<template>
  <div class="min-h-screen bg-gradient-dark flex flex-col">
    <!-- Navbar -->
    <header class="w-full px-6 py-4 flex items-center gap-3 border-b border-white/10">
      <img src="@/assets/logo.svg" alt="" class="w-8 h-8" />
      <span class="text-white font-display font-semibold text-lg">
        Grupo del Valle Garbal
      </span>
    </header>

    <div class="relative flex-1 flex items-center justify-center p-4 py-12">
      <!-- Background decoration -->
      <div class="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          class="absolute -top-40 -right-40 w-80 h-80 bg-violet-500/10 rounded-full blur-3xl"
        ></div>
        <div
          class="absolute -bottom-40 -left-40 w-80 h-80 bg-ink-500/25 rounded-full blur-3xl"
        ></div>
      </div>

      <div class="relative w-full max-w-5xl">
      <div class="grid gap-8 lg:grid-cols-2 lg:items-start">
      <!-- Login Card -->
      <div class="w-full max-w-md mx-auto lg:mx-0 lg:max-w-none">
      <!-- Glass Card -->
      <div class="glass p-8">
        <h2 class="text-xl font-display font-semibold text-ink-900 mb-6">
          {{
            pendingEmailLink
              ? "Confirmar acceso"
              : emailLinkMode
              ? "Acceso sin contraseña"
              : "Iniciar sesión"
          }}
        </h2>

        <!-- Pending Email Link Confirmation -->
        <div v-if="pendingEmailLink" class="space-y-4">
          <p class="text-ink-400 text-sm">
            Detectamos un enlace de acceso. Haz clic en confirmar para
            completar el inicio de sesión.
          </p>

          <div v-if="!emailLinkKnownEmail">
            <label class="block text-sm text-ink-500 mb-2"
              >Correo electrónico</label
            >
            <input
              type="email"
              v-model="pendingEmailLinkEmail"
              class="input-field"
              placeholder="tu@email.com"
              required
            />
          </div>

          <button
            type="button"
            @click="confirmEmailLinkSignIn"
            class="btn-primary w-full flex items-center justify-center gap-2"
            :disabled="loading"
          >
            <svg
              v-if="loading"
              class="w-5 h-5 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>Confirmar acceso</span>
          </button>
        </div>

        <!-- Email Link Mode -->
        <form
          v-else-if="emailLinkMode"
          @submit.prevent="sendEmailLink"
          class="space-y-4"
        >
          <div>
            <label class="block text-sm text-ink-500 mb-2"
              >Correo electrónico</label
            >
            <div class="relative">
              <input
                type="email"
                v-model="username"
                class="input-field pl-11"
                placeholder="tu@email.com"
                required
              />
              <svg
                class="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>

          <button
            type="submit"
            class="btn-primary w-full flex items-center justify-center gap-2"
            :disabled="loading || emailLinkSent"
          >
            <svg
              v-if="loading"
              class="w-5 h-5 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>{{
              emailLinkSent ? "Enlace enviado" : "Enviar enlace"
            }}</span>
          </button>

          <!-- Success message -->
          <div
            v-if="emailLinkSent"
            class="p-4 rounded-xl bg-emerald-50 border border-emerald-200"
          >
            <div class="flex items-center gap-3">
              <svg
                class="w-5 h-5 text-emerald-600 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <p class="text-emerald-700 text-sm">
                Revisa tu correo y haz clic en el enlace para acceder.
              </p>
            </div>
          </div>

          <div class="text-center pt-2">
            <button
              type="button"
              @click="emailLinkMode = false"
              class="text-ink-400 hover:text-violet-600 text-sm transition-colors"
            >
              Usar contraseña
            </button>
          </div>
        </form>

        <!-- Password Mode -->
        <form v-else @submit.prevent="login" class="space-y-4">
          <div>
            <label class="block text-sm text-ink-500 mb-2"
              >Correo electrónico</label
            >
            <div class="relative">
              <input
                type="email"
                v-model="username"
                class="input-field pl-11"
                placeholder="tu@email.com"
                required
              />
              <svg
                class="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>

          <div>
            <label class="block text-sm text-ink-500 mb-2">Contraseña</label>
            <div class="relative">
              <input
                :type="showPassword ? 'text' : 'password'"
                v-model="password"
                class="input-field pl-11 pr-11"
                placeholder="********"
                required
              />
              <svg
                class="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute right-4 top-1/2 -translate-y-1/2 text-ink-300 hover:text-ink-700 transition-colors"
              >
                <svg
                  v-if="showPassword"
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
                <svg
                  v-else
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </button>
            </div>
          </div>

          <button
            type="submit"
            class="btn-primary w-full flex items-center justify-center gap-2"
            :disabled="loading"
          >
            <svg
              v-if="loading"
              class="w-5 h-5 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>Iniciar sesión</span>
          </button>

          <div class="text-center pt-2">
            <button
              type="button"
              @click="emailLinkMode = true"
              class="text-ink-400 hover:text-violet-600 text-sm transition-colors"
            >
              Acceder sin contraseña
            </button>
          </div>
        </form>
      </div>
      </div>

      <!-- Divider (mobile only; side-by-side columns speak for themselves on desktop) -->
      <div class="flex items-center gap-3 text-ink-200 text-xs uppercase tracking-wider lg:hidden">
        <span class="h-px flex-1 bg-white/10"></span>
        Aún sin acceso
        <span class="h-px flex-1 bg-white/10"></span>
      </div>

      <!-- Request Access -->
      <div class="w-full max-w-md mx-auto lg:mx-0 lg:max-w-none">
        <InterestForm />
      </div>
      </div>

      <!-- Footer -->
      <p class="text-center text-ink-200 text-sm mt-8">
        {{ new Date().getFullYear() }} Grupo del Valle Garbal
      </p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import {
  signInWithEmailAndPassword,
  isSignInWithEmailLink,
  signInWithEmailLink,
  sendSignInLinkToEmail
} from "firebase/auth";
import { auth } from "@/firebase";
import { useNotification } from "@/composables/useNotification";
import { useAppStore } from "@/stores/app";
import { useMapStore } from "@/stores/map";
import { useUserStore } from "@/stores/user";
import { useAuthStore } from "@/stores/auth";
import { useDBStore } from "@/stores/db";
import { useAreasStore } from "@/stores/areas";
import { useConfigStore } from "@/stores/config";
import InterestForm from "@/components/Auth/InterestForm.vue";

const EMAIL_FOR_SIGN_IN_KEY = "emailForSignIn";

export default {
  name: "Login",
  components: { InterestForm },
  setup() {
    const router = useRouter();
    const appStore = useAppStore();
    const mapStore = useMapStore();
    const userStore = useUserStore();
    const authStore = useAuthStore();
    const dbStore = useDBStore();
    const areasStore = useAreasStore();
    const configStore = useConfigStore();
    const notify = useNotification();

    // State
    const username = ref("");
    const password = ref("");
    const loading = ref(false);
    const showPassword = ref(false);
    const emailLinkMode = ref(false);
    const emailLinkSent = ref(false);
    const userData = ref({});
    const pendingEmailLink = ref(false);
    const pendingEmailLinkEmail = ref("");
    const emailLinkKnownEmail = ref(false);

    // Methods

    // Only detects the link and asks the user to confirm — does not consume
    // the one-time action code. Auto-consuming it on mount let email
    // security scanners (Outlook Safe Links, corporate link prefetchers)
    // burn the code before the real user clicked, causing
    // auth/invalid-action-code.
    const detectEmailLinkSignIn = () => {
      if (!isSignInWithEmailLink(auth, window.location.href)) {
        return;
      }

      const storedEmail = window.localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY);
      if (storedEmail) {
        pendingEmailLinkEmail.value = storedEmail;
        emailLinkKnownEmail.value = true;
      }
      pendingEmailLink.value = true;
    };

    const confirmEmailLinkSignIn = async () => {
      if (loading.value) {
        return;
      }

      const email = pendingEmailLinkEmail.value.trim();
      if (!email) {
        notify.warning("Por favor ingresa tu correo electrónico");
        return;
      }

      loading.value = true;

      try {
        const result = await signInWithEmailLink(auth, email, window.location.href);
        window.localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);
        username.value = email;
        authStore.setSession(result.user);
        await completeLogin(result.user);
      } catch (error) {
        loading.value = false;
        notify.error("Error al iniciar sesión: " + error.message);
      }
    };

    const sendEmailLink = async () => {
      if (!username.value || username.value.length === 0) {
        notify.warning("Por favor ingresa tu correo electrónico");
        return;
      }

      loading.value = true;

      const actionCodeSettings = {
        url: window.location.origin + "/",
        handleCodeInApp: true
      };

      try {
        await sendSignInLinkToEmail(auth, username.value, actionCodeSettings);
        window.localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, username.value);
        emailLinkSent.value = true;
        loading.value = false;
      } catch (error) {
        loading.value = false;
        notify.error("Error al enviar el enlace: " + error.message);
      }
    };

    const completeLogin = async (firebaseUser) => {
      let status;
      status = await fetchUserData(firebaseUser);

      if (!status) {
        loading.value = false;
        return;
      }

      status = await fetchMapData();

      if (!status) {
        loading.value = false;
        return;
      }

      appStore.toggleLoading();

      // Load AGEB data in parallel
      await mapStore.loadAGEBData();

      appStore.toggleLoading();
      router.push("/dashboard/agebs");
    };

    const login = async () => {
      let authenticated = false;
      loading.value = true;

      if (password.value.length > 0 && username.value.length > 0) {
        authenticated = await authenticateUser();
      } else {
        notify.warning("Completar todos los campos");
        loading.value = false;
        return;
      }

      if (!authenticated) {
        loading.value = false;
        return;
      }

      await completeLogin(authStore.session);
    };

    const authenticateUser = async () => {
      try {
        const userCredential = await signInWithEmailAndPassword(
          auth,
          username.value,
          password.value
        );
        authStore.setSession(userCredential.user);
        return true;
      } catch (error) {
        notify.error(error.message);
        return false;
      }
    };

    const fetchUserData = async (firebaseUser) => {
      try {
        // Ensure config is loaded to check admin status
        await configStore.fetchConfig();

        const uid = firebaseUser.uid;
        const email = firebaseUser.email;
        const isAdmin = configStore.isAdmin(email);

        // Try to get existing user or create new one
        let user = await dbStore.getOrCreateUser(uid);

        if (user) {
          // User exists - update admin status if changed
          userData.value = { ...user, admin: isAdmin || user.admin };
          userStore.setUserData(userData.value);
          return true;
        }

        // User doesn't exist - check if admin (auto-create for admins)
        if (isAdmin) {
          const defaultArea = await areasStore.getDefaultArea();

          userData.value = {
            name: firebaseUser.displayName || "Admin",
            email: email,
            admin: true,
            area: defaultArea || "default"
          };

          await dbStore.getOrCreateUser(uid, userData.value);
          userStore.setUserData(userData.value);
          return true;
        }

        // Regular user without document - error
        notify.error("Error con el usuario, comunicarse con el administrador");
        return false;
      } catch (error) {
        notify.error("Error al obtener datos del usuario: " + error.message);
        return false;
      }
    };

    const fetchMapData = async () => {
      try {
        const areaId = userData.value.area;
        const areaData = await areasStore.getAreaData(areaId);

        if (areaData) {
          // Set area ID first (needed for convention-based paths)
          mapStore.setArea(areaId);
          // Then update map metadata (center, zoom)
          mapStore.updateMapData(areaData);
          return true;
        } else {
          notify.error("Error al obtener datos, comunicarse con el administrador");
          return false;
        }
      } catch (error) {
        notify.error("Error al obtener datos del mapa: " + error.message);
        return false;
      }
    };

    // Lifecycle
    onMounted(() => {
      detectEmailLinkSignIn();
    });

    return {
      username,
      password,
      loading,
      showPassword,
      emailLinkMode,
      emailLinkSent,
      pendingEmailLink,
      pendingEmailLinkEmail,
      emailLinkKnownEmail,
      sendEmailLink,
      confirmEmailLinkSignIn,
      login
    };
  }
};
</script>
